import { useEffect, useRef } from 'react';
import * as THREE from 'three';

/**
 * ThreeBackground — immersive ink-particle field for the hero section.
 * Uses raw Three.js (already in package.json as "three": "^0.178.0").
 * Renders a Points cloud of ~400 particles with:
 *   - Gentle float animation
 *   - Mouse parallax tilt
 *   - Accent colour pulses on ~20% of particles
 * Fully disposed on unmount to prevent memory leaks.
 */
export default function ThreeBackground({ className = '' }) {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    // ─── Scene setup ──────────────────────────────────────────────────
    const W = mount.clientWidth  || window.innerWidth;
    const H = mount.clientHeight || window.innerHeight;

    const renderer = new THREE.WebGLRenderer({
      antialias: false,
      alpha: true,
      powerPreference: 'low-power',
    });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const scene  = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, W / H, 0.1, 1000);
    camera.position.z = 5;

    // ─── Particles ───────────────────────────────────────────────────
    const COUNT   = 420;
    const positions = new Float32Array(COUNT * 3);
    const colors    = new Float32Array(COUNT * 3);
    const sizes     = new Float32Array(COUNT);
    const speeds    = new Float32Array(COUNT);
    const phases    = new Float32Array(COUNT);

    const baseColor   = new THREE.Color('#ffffff');
    const accentColor = new THREE.Color('#ff3c22');

    for (let i = 0; i < COUNT; i++) {
      const i3 = i * 3;
      positions[i3]     = (Math.random() - 0.5) * 14;
      positions[i3 + 1] = (Math.random() - 0.5) * 10;
      positions[i3 + 2] = (Math.random() - 0.5) * 6;

      const isAccent = Math.random() < 0.15;
      const c = isAccent ? accentColor : baseColor;
      colors[i3]     = c.r;
      colors[i3 + 1] = c.g;
      colors[i3 + 2] = c.b;

      sizes[i]  = Math.random() * 2.2 + 0.6;
      speeds[i] = Math.random() * 0.4 + 0.1;
      phases[i] = Math.random() * Math.PI * 2;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('color',    new THREE.BufferAttribute(colors,    3));
    geo.setAttribute('size',     new THREE.BufferAttribute(sizes,     1));

    const mat = new THREE.PointsMaterial({
      size: 0.06,
      vertexColors: true,
      transparent: true,
      opacity: 0.55,
      sizeAttenuation: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    const points = new THREE.Points(geo, mat);
    scene.add(points);

    // ─── Mouse parallax ──────────────────────────────────────────────
    const mouse = { x: 0, y: 0, tx: 0, ty: 0 };

    const onMouseMove = (e) => {
      mouse.tx = (e.clientX / window.innerWidth  - 0.5) * 2;
      mouse.ty = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMouseMove, { passive: true });

    // ─── Resize handler ──────────────────────────────────────────────
    const onResize = () => {
      const w = mount.clientWidth  || window.innerWidth;
      const h = mount.clientHeight || window.innerHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', onResize, { passive: true });

    // ─── Animation loop ──────────────────────────────────────────────
    let rafId;
    const posAttr = geo.getAttribute('position');

    const tick = (t) => {
      rafId = requestAnimationFrame(tick);
      const time = t * 0.001;

      // Smooth mouse follow
      mouse.x += (mouse.tx - mouse.x) * 0.04;
      mouse.y += (mouse.ty - mouse.y) * 0.04;

      // Gentle float of each particle
      for (let i = 0; i < COUNT; i++) {
        const i3   = i * 3;
        const base = positions[i3 + 1]; // original Y
        posAttr.array[i3 + 1] =
          base + Math.sin(time * speeds[i] + phases[i]) * 0.18;
      }
      posAttr.needsUpdate = true;

      // Parallax tilt on group
      points.rotation.y =  mouse.x * 0.06;
      points.rotation.x = -mouse.y * 0.04;

      // Slow global drift
      points.rotation.z = time * 0.015;

      // Pulse opacity
      mat.opacity = 0.45 + Math.sin(time * 0.5) * 0.1;

      renderer.render(scene, camera);
    };
    rafId = requestAnimationFrame(tick);

    // ─── Cleanup ─────────────────────────────────────────────────────
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('resize', onResize);
      geo.dispose();
      mat.dispose();
      renderer.dispose();
      if (mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div
      ref={mountRef}
      className={`three-bg ${className}`}
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    />
  );
}
